import {getArtifactVersionNodeFromUri} from '@wandb/weave/components/PagePanelComponents/Home/common';
import * as HomeQuery from '@wandb/weave/components/PagePanelComponents/Home/query';
import {useMakePublicBoardFromNode} from '@wandb/weave/components/Panel2/pyBoardGen';
import {constString, opGet, opStreamTableRows} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React, {useEffect, useState} from 'react';

import {
  useWeaveServer,
  WeaveVersion,
} from '../../../ComputeGraphContextProvider';
import NoAccessPage from '../../../pages/NoAccessPage';
import {useViewer} from '../../../state/viewer/hooks';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {
  isPublicQueue,
  viewerCanSeePublicQueueAdminTabs,
} from '../QueueDetail/tabUtil';
import {Queue, QueueExternalLinks} from '../Queues/queueUtil';
import {ExternalLink} from '../utilTypes';
import {ObservabilityDashboard} from './Dashboard';
import {ExternalMonitoring} from './ExternalMonitoring';
import {NoStreamTableMessage} from './NoStreamTableMessage';

const SEED_BOARD_OP_NAME = 'py_board-observability';

type TabMonitorProps = {
  queue: Queue;
};

export const TabMonitor = ({queue}: TabMonitorProps) => {
  const viewer = useViewer();
  const weaveVersion = useWeaveServer()[1];
  if (weaveVersion !== WeaveVersion.V1) {
    return <></>;
  }

  if (isPublicQueue(queue) && !viewerCanSeePublicQueueAdminTabs(viewer)) {
    return <NoAccessPage contentKey={'loggedIn'} />;
  }

  return (
    <TabMonitorInner
      entityName={queue.entityName}
      projectName={queue.projectName}
      name={queue.name}
      id={queue.id}
      externalLinks={queue.externalLinks}
    />
  );
};

type TabMonitorInnerProps = {
  entityName: string;
  projectName: string;
  name: string;
  id: string;
  externalLinks?: QueueExternalLinks;
};

const TabMonitorInner = ({
  entityName,
  projectName,
  name,
  id,
  externalLinks,
}: TabMonitorInnerProps) => {
  const [generating, setIsGenerating] = useState(false);
  const [created, setCreated] = useState(false);

  // TODO(gst): remove with artifact ttl scheme
  // HACK: Bump this when merging changes to queue observability template,
  // forces a new board to be created
  const version = 0;

  // "=" and "-" are not valid run names, but get hashed to with base64 encoding
  // replace with "_" to match valid run name set in gorilla
  const streamTableName = btoa(`${name}`)
    .replaceAll('=', '_')
    .replaceAll('/', '-');
  const uri = `wandb-artifact:///${entityName}/${projectName}/${streamTableName}:latest/obj`;
  const boardName = streamTableName + '-board-' + version;
  const boardURI = `wandb-artifact:///${entityName}/${projectName}/${boardName}:latest/obj`;

  const makePublishBoard = useMakePublicBoardFromNode();
  const nodeArtifactVersion = getArtifactVersionNodeFromUri(
    uri,
    'stream_table'
  );
  const dependencyQuery = useNodeValue(nodeArtifactVersion);
  const boards = HomeQuery.useProjectBoards(entityName, projectName);

  useEffect(() => {
    const boardExists = boards.result.find(board => board.name === boardName);
    if (
      !boards.loading &&
      !dependencyQuery.loading &&
      dependencyQuery.result &&
      !boardExists &&
      !generating &&
      !created
    ) {
      const streamTable = opGet({uri: constString(uri)});
      const streamTableRows = opStreamTableRows({
        self: streamTable as any,
      });
      setIsGenerating(true);
      makePublishBoard(
        streamTableRows,
        newPanel => {
          setCreated(true);
        },
        SEED_BOARD_OP_NAME,
        boardName,
        projectName,
        entityName
      );
    }
  }, [
    dependencyQuery.loading,
    dependencyQuery.result,
    boards,
    generating,
    created,
    makePublishBoard,
    uri,
    boardName,
    projectName,
    entityName,
  ]);

  if (boards.loading || dependencyQuery.loading || (generating && !created)) {
    return <Loader active size="large" name="tab-monitor-loader" />;
  }

  if (!dependencyQuery.result) {
    // no streamtable found
    return <NoStreamTableMessage />;
  }
  const links: ExternalLink[] =
    externalLinks != null && 'links' in externalLinks
      ? externalLinks.links
      : [];

  return (
    <>
      <ObservabilityDashboard boardURI={boardURI} />
      <ExternalMonitoring links={links} queueID={id} />
    </>
  );
};
